/** Invite, transfer, sole-Owner and removal dialogs of the Team & roles page. */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";

import { useOrganization } from "@/auth/organization";
import { Alert, Button, ErrorAlert, Field, Form, Input, Modal, Select, useToast } from "@/components/ui";
import { DraftOffer, DraftSavedNote } from "@/components/ui/FormDraft";
import { buildRestrictions, emptySelections, type ScopeDraft } from "@/lib/access";
import { identity } from "@/lib/api";
import { INVITE_DRAFT_KEY, type InviteDraft } from "@/lib/invite-draft";
import { useFormDraft } from "@/lib/form-drafts";
import type { Member, Role } from "@/lib/types";

import { AccessErrorAlert, AccessPreviewPanel, RoleScopes, useAccessResources } from "./AccessEditor";
import { canGrant, customerRoles, describeRole, firstName, OWNER, shortName, splitRoles } from "./roles";
import { ChoiceRow, WhatHappens } from "./ui";

export function useTeamRefresh() {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  return async (opts: { self?: boolean } = {}) => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["members", orgId] }),
      qc.invalidateQueries({ queryKey: ["invitations", orgId] }),
      ...(opts.self
        ? [
            qc.invalidateQueries({ queryKey: ["permissions", orgId] }),
            qc.invalidateQueries({ queryKey: ["organizations"] }),
          ]
        : []),
    ]);
  };
}

const fullName = (first: string, last: string) => `${first.trim()} ${last.trim()}`.trim() || undefined;

function NameFields({
  first,
  last,
  onFirst,
  onLast,
}: {
  first: string;
  last: string;
  onFirst: (v: string) => void;
  onLast: (v: string) => void;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Field label="First name" name="first_name">
        {(p) => <Input {...p} value={first} onChange={(e) => onFirst(e.target.value)} autoComplete="off" />}
      </Field>
      <Field label="Last name" name="last_name">
        {(p) => <Input {...p} value={last} onChange={(e) => onLast(e.target.value)} autoComplete="off" />}
      </Field>
    </div>
  );
}

/** Figma "Invite teammate" and "Owner invitation": the same form, titled by the role chosen. */
export function InviteDialog({
  roles,
  presetOwner = false,
  onClose,
}: {
  roles: Role[];
  presetOwner?: boolean;
  onClose: () => void;
}) {
  const { orgId, current, permissions } = useOrganization();
  const refresh = useTeamRefresh();
  const options = customerRoles(roles).filter((r) => canGrant(r, permissions));
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState(() => {
    const preferred = presetOwner ? OWNER : "org_editor";
    return (
      (options.find((r) => r.key === preferred) ?? options.find((r) => r.key !== OWNER) ?? options[0])?.id ??
      ""
    );
  });
  const role = options.find((r) => r.id === roleId);
  const owner = role?.key === OWNER;
  const [draft, setDraft] = useState<ScopeDraft>({});
  const resources = useAccessResources();
  const roleIds = roleId ? [roleId] : [];
  const restrictions = buildRestrictions(
    roleIds,
    draft,
    new Map(options.map((r) => [r.id, r])),
    resources.narrowable,
  );
  const toast = useToast();
  // PHP-3874: the person's typing (not the access scopes, which depend on the role) is kept as a draft.
  const kept = useFormDraft<InviteDraft>(INVITE_DRAFT_KEY, { first, last, email, roleId });
  const invite = useMutation({
    mutationFn: () =>
      identity.invite(orgId, {
        email: email.trim(),
        role_ids: roleIds,
        name: fullName(first, last),
        restrictions,
      }),
    onSuccess: async () => {
      kept.clear();
      await refresh();
      toast.show({ tone: "success", title: "Invitation sent" });
      onClose();
    },
  });
  return (
    <Modal
      open
      onClose={onClose}
      title={owner ? "Owner invitation" : "Invite teammate"}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="invite"
            loading={invite.isPending}
            disabled={!roleId || emptySelections(restrictions)}
          >
            Send invite
          </Button>
        </>
      }
    >
      <Form
        id="invite"
        className="space-y-4"
        error={invite.error}
        onSubmit={(e) => {
          e.preventDefault();
          invite.mutate();
        }}
      >
        <AccessErrorAlert error={invite.error} />
        <DraftOffer
          draft={kept}
          onContinue={(d) => {
            setFirst(d.first ?? "");
            setLast(d.last ?? "");
            setEmail(d.email ?? "");
            if (options.some((r) => r.id === d.roleId)) setRoleId(d.roleId);
          }}
        />
        <NameFields first={first} last={last} onFirst={setFirst} onLast={setLast} />
        <Field label="Email" name="email" required>
          {(p) => (
            // eslint-disable-next-line jsx-a11y/no-autofocus -- the form is this screen's only task, so focus starts in its first field (PHP-3348)
            <Input {...p} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
          )}
        </Field>
        <Field label="User type" name="role_ids" hint={role && describeRole(role)}>
          {(p) => (
            <Select {...p} value={roleId} onChange={(e) => setRoleId(e.target.value)}>
              {options.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {owner && (
          <Alert tone="warning">
            A second Owner gets full control of {current.organization.name}, including billing and removing
            you.
          </Alert>
        )}
        <DraftSavedNote savedAt={kept.savedAt} />
        {role && <RoleScopes roles={[role]} draft={draft} onChange={setDraft} resources={resources} />}
        {role && (
          <AccessPreviewPanel
            roleIds={roleIds}
            restrictions={restrictions}
            resources={resources}
            who="They"
            invitation
          />
        )}
      </Form>
    </Modal>
  );
}

const SOMEONE_NEW = "new";

/** Figma "Transfer Ownership": offer ownership to a teammate or someone new. */
export function TransferOwnershipDialog({ members, onClose }: { members: Member[]; onClose: () => void }) {
  const { orgId, current } = useOrganization();
  const refresh = useTeamRefresh();
  const candidates = members.filter((m) => splitRoles(m).main?.key !== OWNER);
  const [choice, setChoice] = useState(candidates[0]?.user.id ?? SOMEONE_NEW);
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const teammate = candidates.find((m) => m.user.id === choice);
  const toast = useToast();
  const transfer = useMutation({
    mutationFn: () =>
      identity.transferOwnership(
        orgId,
        teammate ? { email: teammate.user.email } : { email: email.trim(), name: fullName(first, last) },
      ),
    onSuccess: async () => {
      await refresh();
      toast.show({ tone: "success", title: "Ownership transfer started" });
      onClose();
    },
  });
  return (
    <Modal
      open
      onClose={onClose}
      title="Transfer ownership"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="transfer" loading={transfer.isPending}>
            Send transfer request
          </Button>
        </>
      }
    >
      <Form
        id="transfer"
        className="space-y-4"
        error={transfer.error}
        onSubmit={(e) => {
          e.preventDefault();
          transfer.mutate();
        }}
      >
        <ErrorAlert error={transfer.error} />
        <p className="text-sm text-fg-muted">
          They become Owner of {current.organization.name} only after they accept by e-mail. You stay Owner
          until then, and become a Business Administrator afterwards.
        </p>
        <Field label="New Owner" required>
          {(p) => (
            <Select {...p} value={choice} onChange={(e) => setChoice(e.target.value)}>
              {candidates.map((m) => (
                <option key={m.user.id} value={m.user.id}>
                  {shortName(m.user.display_name, m.user.email)} · {m.user.email}
                </option>
              ))}
              <option value={SOMEONE_NEW}>Someone new…</option>
            </Select>
          )}
        </Field>
        {!teammate && (
          <>
            <NameFields first={first} last={last} onFirst={setFirst} onLast={setLast} />
            <Field label="Email" name="email" required>
              {(p) => <Input {...p} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />}
            </Field>
          </>
        )}
      </Form>
    </Modal>
  );
}

/** Figma "You're the only Owner": why you can't leave or step down yet, and the two ways out. */
export function OnlyOwnerDialog({
  onTransfer,
  onAddOwner,
  onClose,
}: {
  onTransfer: () => void;
  onAddOwner: () => void;
  onClose: () => void;
}) {
  const { current } = useOrganization();
  return (
    <Modal
      open
      onClose={onClose}
      title="You're the only Owner"
      footer={<Button onClick={onClose}>Got it</Button>}
    >
      <p className="text-sm text-fg-muted">
        {current.organization.name} must always have at least one Owner, so you can't leave or change your own
        role yet.
      </p>
      <div className="space-y-2">
        <ChoiceRow
          title="Transfer ownership"
          description="Choose a teammate. They become Owner only after they accept, and you stay Owner until then."
          onClick={onTransfer}
        />
        <ChoiceRow
          title="Add a second Owner"
          description="Invite someone as Owner, then you can change your own role."
          onClick={onAddOwner}
        />
      </div>
    </Modal>
  );
}

/** Figma "Remove …?": also used to leave the business yourself. */
export function RemoveMemberDialog({
  member,
  self,
  onClose,
  onRemoved,
}: {
  member: Member;
  self: boolean;
  onClose: () => void;
  onRemoved: () => void;
}) {
  const { orgId, current } = useOrganization();
  const refresh = useTeamRefresh();
  const navigate = useNavigate();
  const name = shortName(member.user.display_name, member.user.email);
  const first = firstName(member.user.display_name, member.user.email);
  const role = splitRoles(member).main;
  const business = current.organization.name;
  const toast = useToast();
  const remove = useMutation({
    mutationFn: () => identity.removeMember(orgId, member.user.id),
    onSuccess: async () => {
      if (self) {
        await refresh({ self: true });
        toast.show({ tone: "success", title: "You left the business" });
        navigate("/", { replace: true });
        return;
      }
      await refresh();
      toast.show({ tone: "success", title: "Member removed" });
      onRemoved();
    },
  });
  return (
    <Modal
      open
      onClose={onClose}
      title={self ? `Leave ${business}?` : `Remove ${name} from ${business}?`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>
            {self ? "Leave" : `Remove ${first}`}
          </Button>
        </>
      }
    >
      <ErrorAlert error={remove.error} />
      <p className="text-sm text-fg-muted">
        {self
          ? `You lose access to this business. An Owner can invite you again later.`
          : `${first} is ${role ? `a ${role.name}` : "a member"}. They lose access to this business.`}
      </p>
      <WhatHappens
        items={[
          self ? "Your access ends straight away" : `Access ends straight away for ${first}`,
          "Websites, domains and billing stay with the business",
          "Past changes stay in the audit log",
        ]}
      />
    </Modal>
  );
}
